import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, AlertModal } from '../AlertModal';
import { IconSwords, IconBarChart, IconSettings, IconTrash, IconClipboard } from '../Icons';
import { formatLocalDate } from '../../utils/timeUtils';
import CreatedBy from '../CreatedBy';
import SeasonMatches from '../SeasonMatches';
import SeasonBoard from '../leaderboard/SeasonBoard';
import Brackets from '../Brackets';
import SeasonSettingsForm from './SeasonSettingsForm';
import { seasonPhase, getPrefixColorOption } from './seasonForm';
import './seasons.css';

// The old Stats tab is part of the leaderboard now (/stats links open it)
const TABS = ['overview', 'matches', 'leaderboard', 'brackets', 'settings'];

/**
 * One season's admin page: /admin/seasons/:id[/:tab]. The old per-season pages
 * (/matches, /leaderboard, /brackets) are now tabs of it, at the same URLs.
 */
export default function SeasonPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id || params.seasonId;
  const tab = params.tab === 'stats' ? 'leaderboard' : TABS.includes(params.tab) ? params.tab : 'overview';
  const [season, setSeason] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [visited, setVisited] = useState(() => new Set([tab]));
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [confirm, setConfirm] = useState({ show: false });
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`/api/seasons/${id}`);
      setSeason(res.data); setStatus('ok');
    } catch (err) {
      setStatus(err.response && err.response.status === 404 ? 'notfound' : 'error');
    }
  }, [id]);
  useEffect(() => { setStatus('loading'); load(); }, [load]);
  useEffect(() => { setVisited(v => (v.has(tab) ? v : new Set([...v, tab]))); }, [tab]);

  const go = (next) => navigate(next === 'overview' ? `/admin/seasons/${id}` : `/admin/seasons/${id}/${next}`);

  const del = () => setConfirm({
    show: true,
    message: t('createSeason.deleteConfirm', 'Delete the season "{{name}}"?', { name: season.name }),
    onConfirm: async () => {
      setConfirm({ show: false });
      try {
        await axios.delete(`/api/seasons/${id}`);
        navigate('/admin/seasons');
      } catch (err) {
        setAlert({ show: true, message: err.response?.data?.error || t('seasonPage.deleteFailed', 'Failed to delete season'), type: 'error' });
      }
    },
  });

  if (status === 'loading') return <div className="sp-page"><div className="sp-card sp-empty">{t('seasonPage.loading', 'Loading season…')}</div></div>;
  if (status !== 'ok') {
    return (
      <div className="sp-page">
        <div className="sp-card sp-empty">
          <div className="sp-empty-title">{status === 'notfound' ? t('brackets.seasonNotFound', 'Season Not Found') : t('seasonPage.loadFailed', 'Could not load the season')}</div>
          <div className="sp-muted">{status === 'notfound' ? t('brackets.seasonNotFoundHint', "This season doesn't exist or doesn't belong to you.") : ''}</div>
          <div className="sp-empty-actions">
            {status === 'error' && <button className="sp-btn" onClick={() => { setStatus('loading'); load(); }}>{t('common.retry', 'Retry')}</button>}
            <button className="sp-btn ghost" onClick={() => navigate('/admin/seasons')}>← {t('nav.seasons', 'Seasons')}</button>
          </div>
        </div>
      </div>
    );
  }

  const phase = seasonPhase(season);
  const phaseLabel = { ongoing: t('seasons.ongoing', 'Ongoing'), upcoming: t('seasonPage.upcoming', 'Upcoming'), finished: t('matchStatus.finished', 'Finished') }[phase];
  const tabLabel = {
    overview: t('seasonPage.tabOverview', 'Overview'),
    matches: t('nav.matches', 'Matches'),
    leaderboard: t('leaderboard.title', 'Leaderboard'),
    brackets: t('nav.brackets', 'Brackets'),
    settings: t('seasonPage.tabSettings', 'Settings'),
  };

  return (
    <div className="sp-page">
      {/* Header */}
      <div className="sp-card sp-head">
        <div className="sp-head-top">
          <button className="sp-back" onClick={() => navigate('/admin/seasons')}>← {t('nav.seasons', 'Seasons')}</button>
          <div className="sp-head-actions">
            <button className="sp-btn" onClick={() => navigate(`/admin/matches/new?season=${season.id}`)}>+ {t('seasonPage.addMatch', 'Add match')}</button>
            <button className="sp-btn ghost danger" onClick={del} aria-label={t('seasonPage.deleteSeason', 'Delete season')} title={t('seasonPage.deleteSeason', 'Delete season')}><IconTrash size={13} /></button>
          </div>
        </div>
        <div className="sp-title-row">
          <h1>{season.name}</h1>
          <span className={`sp-phase ${phase}`}>{phaseLabel}</span>
        </div>
        <div className="sp-meta">
          {season.start_date && season.end_date && <span className="sp-chip blue">{formatLocalDate(season.start_date)} → {formatLocalDate(season.end_date)}</span>}
          <span className="sp-chip blue mono">BO{season.best_of}</span>
          <span className={`sp-chip ${season.mode === 'wingman' ? 'purple' : 'green'}`}>{season.mode === 'wingman' ? 'Wingman' : 'Active Duty'}</span>
          {season.veto_enabled ? <span className="sp-chip purple">Veto · {season.veto_style === 'faceit' ? 'Faceit' : 'VRS'}</span> : null}
          <span className="sp-muted">{t('seasonPage.created', 'Created {{date}}', { date: formatLocalDate(season.created_at) })}</span>
          <CreatedBy steamId={season.created_by_steam} name={season.created_by_name} />
        </div>
        <div className="sp-tabs" role="tablist">
          {TABS.map(k => (
            <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => go(k)}>
              {tabLabel[k]}{k === 'settings' && settingsDirty ? <span className="sp-dirty-dot" title={t('seasonPage.unsaved', 'Unsaved changes')} /> : null}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' && <SeasonOverview season={season} onTab={go} />}
      {tab === 'matches' && <SeasonMatches embedded seasonId={season.id} />}
      {tab === 'leaderboard' && <div className="sp-card"><SeasonBoard seasonId={season.id} /></div>}
      {tab === 'brackets' && <Brackets embedded seasonId={season.id} bracketId={params.sub} />}
      {/* Settings stays mounted once opened, so switching tabs never loses unsaved edits */}
      {visited.has('settings') && (
        <div style={{ display: tab === 'settings' ? 'block' : 'none' }}>
          <SeasonSettingsForm season={season} onDirtyChange={setSettingsDirty} onSaved={(s) => setSeason(s)} />
        </div>
      )}

      <ConfirmModal show={confirm.show} message={confirm.message} onConfirm={confirm.onConfirm} onClose={() => setConfirm({ show: false })} />
      <AlertModal show={alert.show} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, show: false })} />
    </div>
  );
}

const STATUS_ORDER = { live: 0, pending: 1, finished: 2, cancelled: 3 };

function SeasonOverview({ season, onTab }) {
  const { t } = useTranslation();
  const [matches, setMatches] = useState(null);
  const [board, setBoard] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`/api/seasons/${season.id}/matches`).then(r => { if (alive) setMatches(r.data || []); }).catch(() => { if (alive) setMatches([]); });
    axios.get(`/api/seasons/${season.id}/leaderboard`).then(r => { if (alive) setBoard(r.data || null); }).catch(() => { if (alive) setBoard({ players: [], teams: [] }); });
    return () => { alive = false; };
  }, [season.id]);

  const count = (st) => (matches || []).filter(m => m.status === st).length;
  const recent = [...(matches || [])].sort((a, b) => (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || (b.id - a.id)).slice(0, 6);
  const topPlayers = [...((board && board.players) || [])].sort((a, b) => (b.hltv_rating || 0) - (a.hltv_rating || 0)).slice(0, 5);
  const topTeams = ((board && board.teams) || []).slice(0, 5);

  const rules = [
    ['admin_only_start', t('createMatch.adminOnlyStart', 'Admin Only Start')], ['enforce_teams', t('createMatch.enforceRosters', 'Enforce Rosters')],
    ['allow_stop', t('createMatch.allowStop', 'Allow !stop Command')], ['skip_backup_load', t('createMatch.skipBackup', 'Skip Backup Load')],
    ['custom_avatars', t('createMatch.customAvatars', 'Custom Avatars')], ['random_password', t('createMatch.randomPassword', 'Random Password Protection')],
    ['show_killfeed', t('createSeason.showKillfeed', 'Show Killfeed')], ['cstv1_public', t('createSeason.cstv1Public', 'CSTV 1 Public')],
    ['cstv2_public', t('createSeason.cstv2Public', 'CSTV 2 Public')], ['halftime_break_enabled', t('createSeason.halftimeBreak', 'Halftime Break')],
    ['ts_enabled', 'TeamSpeak'], ['akros_enabled', 'Akros AC'], ['hltv_enabled', 'HLTV'],
  ].filter(([k]) => season[k]).map(([, l]) => l);
  const prefix = season.chat_prefix ? getPrefixColorOption(season.chat_prefix_color) : null;
  const statusLabel = (s) => t(`matchStatus.${s}`, s);

  return (
    <div className="sp-overview">
      <div className="sp-stats">
        {[
          { k: 'total', label: t('nav.matches', 'Matches'), v: matches ? matches.length : '…' },
          { k: 'live', label: statusLabel('live'), v: matches ? count('live') : '…' },
          { k: 'pending', label: statusLabel('pending'), v: matches ? count('pending') : '…' },
          { k: 'finished', label: statusLabel('finished'), v: matches ? count('finished') : '…' },
        ].map(s => (
          <div key={s.k} className={`sp-card sp-stat ${s.k}`}>
            <div className="sp-stat-v">{s.v}</div>
            <div className="sp-stat-l">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="sp-ov-grid">
        <div className="sp-card">
          <div className="sp-card-head"><h3><IconSwords size={13} /> {t('seasonPage.recentMatches', 'Live & recent matches')}</h3><button className="sp-link" onClick={() => onTab('matches')}>{t('seasonPage.viewAll', 'View all')} →</button></div>
          {!matches ? <div className="sp-muted sp-pad">{t('seasonMatches.loading', 'Loading matches...')}</div>
            : recent.length === 0 ? <div className="sp-muted sp-pad">{t('seasonMatches.noMatchesHint', 'Matches created with this season preset will appear here')}</div>
              : recent.map(m => (
                <Link key={m.id} to={`/admin/matches/${m.id}`} className="sp-match">
                  <span className="sp-match-id">#{m.id}</span>
                  <span className="sp-match-teams">{m.team1_name || 'Team 1'} <em>vs</em> {m.team2_name || 'Team 2'}</span>
                  {(m.status === 'live' || m.status === 'finished') && <span className="sp-match-score">{m.best_of > 1 ? `${m.map_wins_team1 || 0}-${m.map_wins_team2 || 0}` : `${m.score_team1 || 0}-${m.score_team2 || 0}`}</span>}
                  <span className={`sp-status ${m.status}`}>{statusLabel(m.status)}</span>
                </Link>
              ))}
        </div>

        <div className="sp-card">
          <div className="sp-card-head"><h3><IconBarChart size={13} /> {t('seasonPage.topPlayers', 'Top players')}</h3><button className="sp-link" onClick={() => onTab('leaderboard')}>{t('leaderboard.title', 'Leaderboard')} →</button></div>
          {!board ? <div className="sp-muted sp-pad">{t('leaderboard.loading', 'Loading leaderboard...')}</div>
            : topPlayers.length === 0 ? <div className="sp-muted sp-pad">{t('seasonPage.noStats', 'No stats yet - they appear once matches are played.')}</div>
              : (
                <table className="sp-table">
                  <thead><tr><th>#</th><th>{t('seasonPage.player', 'Player')}</th><th>K/D</th><th>ADR</th><th title="HLTV 2.0">{t('seasonPage.rating', 'Rating')}</th></tr></thead>
                  <tbody>{topPlayers.map((p, i) => (
                    <tr key={p.steam_id}><td>{i + 1}</td><td><div className="sp-strong">{p.name}</div><div className="sp-muted sp-small">{p.team_name || ''}</div></td><td>{p.kd}</td><td>{p.adr}</td><td className="sp-strong">{Number(p.hltv_rating || 0).toFixed(2)}</td></tr>
                  ))}</tbody>
                </table>
              )}
          {topTeams.length > 0 && (
            <>
              <div className="sp-sub">{t('seasonPage.standings', 'Standings')}</div>
              <table className="sp-table">
                <thead><tr><th>#</th><th>{t('seasonPage.team', 'Team')}</th><th title={t('seasonPage.mapsWl', 'Maps won–lost')}>{t('seasonPage.mapsWlShort', 'Maps')}</th><th>{t('seasonPage.roundDiff', 'RD')}</th></tr></thead>
                <tbody>{topTeams.map((tm, i) => (
                  <tr key={tm.team_id || tm.name}><td>{i + 1}</td><td className="sp-strong">{tm.name}</td><td>{tm.map_wins}–{tm.map_losses}</td><td>{tm.round_diff > 0 ? `+${tm.round_diff}` : tm.round_diff}</td></tr>
                ))}</tbody>
              </table>
            </>
          )}
        </div>

        <div className="sp-card">
          <div className="sp-card-head"><h3><IconSettings size={13} /> {t('seasonPage.setup', 'Setup')}</h3><button className="sp-link" onClick={() => onTab('settings')}>{t('seasonPage.editSettings', 'Edit settings')} →</button></div>
          <div className="sp-chips">
            {rules.length ? rules.map(l => <span key={l} className="sp-chip amber">{l}</span>) : <span className="sp-chip">{t('seasonPage.defaultSettings', 'Default settings')}</span>}
            {prefix && <span className="sp-chip mono" style={{ color: prefix.hex, borderColor: `${prefix.hex}55` }}>[{season.chat_prefix}]</span>}
            {season.tech_budget_seconds > 0 && <span className="sp-chip">{t('seasonPage.techBudgetChip', 'Tech pause {{min}} min', { min: Math.round(season.tech_budget_seconds / 60) })}</span>}
            {season.stream_delay > 0 && <span className="sp-chip">{t('seasonPage.delayChip', 'Delay {{sec}}s', { sec: season.stream_delay })}</span>}
          </div>
          <div className="sp-sub">{t('seasons.tempAdmins', 'Temp admins')}</div>
          {(season.temp_admins || []).length === 0
            ? <div className="sp-muted sp-small">{t('seasonPage.noTempAdmins', 'None - add them in Settings to give them admin commands in every match of this season.')}</div>
            : <div className="sp-chips">{season.temp_admins.map(a => <span key={a.steam_id} className="sp-chip" title={a.steam_id}>{a.name || a.steam_id}</span>)}</div>}
        </div>

        <div className="sp-card">
          <div className="sp-card-head"><h3><IconClipboard size={13} /> {t('seasonPage.quickActions', 'Quick actions')}</h3></div>
          <div className="sp-actions">
            <Link className="sp-action" to={`/admin/matches/new?season=${season.id}`}>+ {t('seasonPage.addMatch', 'Add match')}<span>{t('seasonPage.addMatchHint', 'Create Match with this season preset')}</span></Link>
            <button className="sp-action" onClick={() => onTab('brackets')}>{t('nav.brackets', 'Brackets')}<span>{t('seasonPage.bracketsHint', 'Create and run the season’s brackets')}</span></button>
            <a className="sp-action" href={`/season/${season.id}`} target="_blank" rel="noreferrer">{t('seasonPage.publicLeaderboard', 'Public leaderboard')} ↗<span>{t('seasonPage.publicLeaderboardHint', 'The page viewers see')}</span></a>
          </div>
        </div>
      </div>
    </div>
  );
}
